import React from 'react';

import { Card, List, ListSubheader, Divider, IconButton } from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import { ReusableTrendsGraph, ReusableTrendsCardHeader } from '../ReusableTrendsGraph';
import { DeviceTrendChannelList } from './DeviceTrendChannelList';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { isDataLoaded, isDataLoading, isDataError, makeLoadedData, Data } from '../../types/data-providers';
import { useDeviceTrendData } from '../../data/DataProviders/DeviceTrendDataProvider';

import {
    TrendsGraphContentLoader,
    TrendsCardHeaderNotLoaded,
    TrendsListLoader,
} from '../ReusableTrendsGraph/ReusableTrendCardContentLoader';
import { DataEmptyIndicator } from '../DataEmptyIndicator';
import { DataErrorIndicator } from '../DataErrorIndicator';
import { useSelector, useDispatch } from 'react-redux';
import { Selectors } from '../../selectors';
import { Actions, Thunks } from '../../actions';
import { Widgets } from '@mui/icons-material';
//import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import Drawer from '@mui/material/Drawer';
import { CustomizeChannels } from './CustomizeChannels';
import { useTrendChannels } from '../../data/DataProviders/TrendChannelsProvider';
import { useAuthHelper } from '../AuthContextProvider';

const StyledDrawer = withStyles({
    root: {
        '& .MuiDrawer-paper': {
            width: '350px',
            overflowX: 'hidden',
        },
    },
})(Drawer);

export const DeviceTrendsCard: React.FC<{
    large?: boolean;
    deviceId: string;
    deviceName: string;
}> = (props) => {
    const { t } = useLanguageLocale();

    const DevicetrendsData = useDeviceTrendData();
    const trendChannels = useTrendChannels();
    const Dispatch = useDispatch();
    const AuthHelper = useAuthHelper();
    const csvUrl = useSelector(Selectors.TrendExport.trendExportUrl);

    const trendsData = DevicetrendsData.trendData;
    const [drawerState, setDrawerState] = React.useState(false);
    const [selectedChartdata, setSelectedChartData] = React.useState<
        Data<
            Array<{
                id: number;
                channelName: string;
                deviceId: string;
                deviceName: string;
                value: Array<{
                    dateTime: string;
                    value: string;
                    isNewAlarm?: boolean;
                    alarmCount?: number;
                }>;
                unit?: string | null;
                color?: any;
            }>
        >
    >();

    React.useEffect(() => {
        DevicetrendsData.fetchTrendData([]);
    }, []);
    React.useEffect(() => {
        if (isDataLoaded(trendsData)) {
            if (trendsData.value.length > 0) {
                const chartValue = makeLoadedData([trendsData.value[0]]);

                setSelectedChartData(chartValue);
            }
        }
    }, [trendsData]);

    React.useEffect(() => {
        if (csvUrl !== '') window.open(csvUrl, '_self');
        return (): void => {
            Dispatch(Actions.TrendExport.trendExportUnmounted());
        };
    }, [csvUrl, Dispatch]); // eslint-disable-line react-hooks/exhaustive-deps
    const toggleDrawer = (open: boolean) => (event: any): void => {
        if (event && event.type && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }

        setDrawerState(open);
    };

    const save = (): void => {
        setDrawerState(false);
    };
    const selectedChannel = (list: any): void => {
        const chartValue = makeLoadedData(list);
        setSelectedChartData(chartValue);
    };

    const downloadCSV = (channels: number[], days: number): void => {
        if (isDataLoaded(trendsData)) {
            Dispatch(Thunks.TrendExport.getTrendsDeviceExportData(props.deviceId, days, channels, AuthHelper));
        }
    };

    return (
        <>
            <Card style={{ margin: '15px', marginTop: 5, marginBottom: 25, flex: 1 }}>
                {isDataLoading(trendsData) && (
                    <List
                        disablePadding
                        subheader={
                            <ReusableTrendsCardHeader
                                title={t('DASHBOARD.SITE_DETAILS.TRENDS_CARD.TRENDVIEWER')}
                                isDevice={true}
                            >
                                <TrendsCardHeaderNotLoaded />
                            </ReusableTrendsCardHeader>
                        }
                    >
                        <TrendsGraphContentLoader style={{ height: '395.5px' }} />
                    </List>
                )}

                {isDataLoaded(trendsData) && isDataLoaded(trendChannels) && (
                    <>
                        {trendsData.value.length === 0 && (
                            <List
                                disablePadding
                                subheader={
                                    <ReusableTrendsCardHeader
                                        title={t('DASHBOARD.SITE_DETAILS.TRENDS_CARD.TRENDVIEWER')}
                                        isDevice={true}
                                    >
                                        <TrendsCardHeaderNotLoaded />
                                    </ReusableTrendsCardHeader>
                                }
                            >
                                <DataEmptyIndicator
                                    icon="ShowChart"
                                    message={t('DASHBOARD.NO_TRENDS')}
                                    data={trendsData}
                                    isEmpty={trendsData.value.length === 0}
                                    inline
                                />
                            </List>
                        )}
                        {isDataLoaded(selectedChartdata) && (
                            <ReusableTrendsGraph
                                large
                                style={{ height: '400px' }}
                                trendsData={selectedChartdata}
                                isDevice={true}
                                fetchTrendData={DevicetrendsData.fetchTrendData}
                                downloadCSV={downloadCSV}
                                channels={trendChannels.value}
                                deviceId={props.deviceId}
                                downloadFileName={props.deviceName}
                            />
                        )}
                    </>
                )}

                {isDataError(trendsData) && (
                    <List
                        disablePadding
                        subheader={
                            <ReusableTrendsCardHeader title={t('DASHBOARD.SITE_DETAILS.TRENDS_CARD.TRENDVIEWER')}>
                                <TrendsCardHeaderNotLoaded />
                            </ReusableTrendsCardHeader>
                        }
                    >
                        <DataErrorIndicator data={trendsData} message={t('DASHBOARD.ERROR_LOADING_TRENDS')} inline />
                    </List>
                )}
            </Card>
            <Card
                style={{
                    width: '300px',
                    margin: '5px',
                    marginBottom: 25,
                    marginRight: 15,
                }}
            >
                <List
                    disablePadding
                    subheader={
                        <ListSubheader
                            color="primary"
                            component="div"
                            style={{
                                fontWeight: 600,
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            {t('DASHBOARD.SITE_DETAILS.TRENDS_CARD.CHANNELS')}
                            <IconButton data-testid="trends-channels" onClick={toggleDrawer(true)} size="large">
                                <Widgets />
                            </IconButton>
                        </ListSubheader>
                    }
                >
                    <Divider />

                    {isDataLoading(trendsData) && (
                        <>
                            <TrendsListLoader />
                        </>
                    )}

                    {isDataError(trendsData) && (
                        <>
                            <DataErrorIndicator
                                data={trendsData}
                                message={t('DASHBOARD.ERROR_LOADING_CHANNELS')}
                                inline
                            />
                        </>
                    )}

                    {isDataLoaded(trendsData) && (
                        <>
                            <DataEmptyIndicator
                                icon="List"
                                message={t('DASHBOARD.NO_CHANNELS')}
                                data={trendsData}
                                isEmpty={trendsData.value.length === 0}
                                inline
                            />

                            <DeviceTrendChannelList trends={trendsData.value} selectedChannel={selectedChannel} />
                        </>
                    )}
                </List>
            </Card>

            <StyledDrawer anchor="right" open={drawerState} onClose={toggleDrawer(false)} style={{ width: '350px' }}>
                {isDataLoading(trendChannels) && (
                    <>
                        <TrendsListLoader />
                    </>
                )}

                {isDataError(trendChannels) && (
                    <>
                        <DataErrorIndicator
                            data={trendChannels}
                            message={t('DASHBOARD.ERROR_LOADING_CHANNELS')}
                            inline
                        />
                    </>
                )}
                {isDataLoaded(trendChannels) && (
                    <>
                        <DataEmptyIndicator
                            icon="List"
                            message={t('DASHBOARD.NO_CHANNELS')}
                            data={trendChannels}
                            isEmpty={trendChannels.value.trendChannels.length === 0}
                            inline
                        />{' '}
                        <CustomizeChannels
                            deviceId={props.deviceId}
                            toggleDrawer={toggleDrawer}
                            channels={trendChannels.value}
                            save={save}
                        />
                    </>
                )}
            </StyledDrawer>
        </>
    );
};
